/**
 * Servizio Circolari - lato Associato
 * Chiamate REST verso /api/circolari/* con token OTP associato.
 * `creaCircolariApi` costruisce le stesse chiamate per altri archivi
 * (rappresentanti: services/circolariRappresentanteService.ts).
 * Se l'associato non è in regola con la quota le route rispondono
 * 403 { code: 'NON_IN_REGOLA' }: usare `isNonInRegola(error)`.
 */

import axios from 'axios';
import { getAuthHeader } from './otpAuthService';
import { etichettaNumero, type CategoriaArchivio, type SuffissoCircolare } from '../utils/circolariEtichette';

// REST: usare REACT_APP_API_BASE_URL (include già /api), NON REACT_APP_API_URL (GraphQL).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const BASE = `${API_BASE_URL}/circolari`;

// --- Tipi ---

/** Categorie della VISIBILITÀ (chi vede la circolare), non la `categoria` dell'archivio. */
export type SettoreVisibilita = 'STUDIO' | 'AUTOSCUOLA' | 'NAUTICA';

export const SETTORI_VISIBILITA: { value: SettoreVisibilita; label: string }[] = [
  { value: 'STUDIO', label: 'Studi di consulenza' },
  { value: 'AUTOSCUOLA', label: 'Autoscuole' },
  { value: 'NAUTICA', label: 'Nautica' },
];

export interface CircolareAllegato {
  filename: string;
  originalName: string;
  /** PDF, Word (.docx) o Excel (.xlsx) */
  mimeType?: string;
  size?: number;
}

export interface CircolareVisibilita {
  tipo: 'tutti' | 'categorie';
  categorie?: string[];
  soloReteCertificata?: boolean;
}

/** Riga dell'elenco: il backend non manda la nota. */
export interface CircolareListItem {
  _id: string;
  /** Assente nelle voci storiche senza lettera UNASCA */
  numero?: number | null;
  suffisso?: SuffissoCircolare | null;
  anno: number;
  /** Assente = circolari_norme */
  categoria?: CategoriaArchivio;
  oggetto: string;
  dataCircolare: string;
  allegati?: CircolareAllegato[];
  visibilita?: CircolareVisibilita;
  pubblicataIl?: string;
  enteEmittente?: string;
  protocollo?: string;
  tipoDocumento?: string;
}

export interface CircolareDetail extends CircolareListItem {
  nota: string;
  allegati: CircolareAllegato[];
}

export interface PaginatedCircolari {
  items: CircolareListItem[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CircolariQuery {
  page?: number;
  limit?: number;
  anno?: number | string;
  /** Categoria dell'archivio (circolari_norme, comunicati, sted, documento_unico, materiale_associativo) */
  categoria?: CategoriaArchivio | '';
  search?: string;
}

// --- Funzioni ---

/** Vero se l'errore è il 403 «non in regola con la quota». */
export const isNonInRegola = (error: any): boolean =>
  error?.response?.status === 403 && error?.response?.data?.code === 'NON_IN_REGOLA';

/**
 * Vero se il server risponde 503 con il codice CIRCOLARI_DISATTIVATE: modulo
 * circolari spento temporaneamente. Un 503 senza codice (per esempio un proxy
 * durante un riavvio) è un errore ordinario.
 */
export const isCircolariDisattivate = (error: any): boolean =>
  error?.response?.status === 503 && error?.response?.data?.code === 'CIRCOLARI_DISATTIVATE';

/** Numero leggibile: «n. 479/SN/2026», «n. 12/2026», «prot. 16883 · ACI-PRA» o «senza numero» (utils/circolariEtichette). */
export const formatNumeroCircolare = etichettaNumero;

/** Chiamate REST di un archivio circolari (associato o rappresentante). */
export interface CircolariApi {
  getCircolari: (params?: CircolariQuery) => Promise<PaginatedCircolari>;
  getAnniCircolari: () => Promise<number[]>;
  getCircolare: (id: string) => Promise<CircolareDetail>;
  openAllegatoCircolare: (circolareId: string, allegato: CircolareAllegato) => Promise<void>;
  scaricaAllegatoCircolare: (circolareId: string, allegato: CircolareAllegato) => Promise<void>;
}

export interface OpzioniCircolariApi {
  /** URL REST dell'archivio, senza barra finale (es. `${API_BASE_URL}/circolari`) */
  base: string;
  /** Intestazioni di autenticazione, lette a ogni chiamata */
  headers: () => Record<string, string>;
}

/**
 * Fabbrica delle chiamate REST di un archivio circolari: stesse risposte del
 * backend (routes/lib/circolariLettura.js) per associati e rappresentanti.
 */
export const creaCircolariApi = ({ base, headers }: OpzioniCircolariApi): CircolariApi => {
  const urlAllegato = (circolareId: string, allegato: CircolareAllegato) =>
    `${base}/${encodeURIComponent(circolareId)}/allegati/${encodeURIComponent(allegato.filename)}`;

  /**
   * Elenco paginato delle circolari visibili.
   * Risposta del backend: { success, data: [...], total, page, totalPages }.
   */
  const getCircolari = async (params: CircolariQuery = {}): Promise<PaginatedCircolari> => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.anno) query.append('anno', String(params.anno));
    if (params.categoria) query.append('categoria', params.categoria);
    if (params.search && params.search.trim()) query.append('search', params.search.trim());

    const qs = query.toString();
    const response = await axios.get(`${base}${qs ? `?${qs}` : ''}`, {
      headers: headers() as any,
    });
    const body = response.data || {};
    const items: CircolareListItem[] = Array.isArray(body.data) ? body.data : [];
    return {
      items,
      total: typeof body.total === 'number' ? body.total : items.length,
      page: typeof body.page === 'number' ? body.page : params.page || 1,
      totalPages: typeof body.totalPages === 'number' ? body.totalPages : 1,
    };
  };

  /** Anni in cui esistono circolari visibili, dal più recente. */
  const getAnniCircolari = async (): Promise<number[]> => {
    const response = await axios.get(`${base}/anni`, { headers: headers() as any });
    return Array.isArray(response.data?.data) ? response.data.data : [];
  };

  /** Dettaglio di una circolare (404 se non esiste o non è visibile). */
  const getCircolare = async (id: string): Promise<CircolareDetail> => {
    const response = await axios.get(`${base}/${encodeURIComponent(id)}`, {
      headers: headers() as any,
    });
    return response.data.data;
  };

  /**
   * Scarica un allegato tramite endpoint autenticato e lo apre in una nuova
   * scheda (la navigazione diretta non porterebbe l'intestazione Authorization).
   */
  const openAllegatoCircolare = async (
    circolareId: string,
    allegato: CircolareAllegato
  ): Promise<void> => {
    // Apri la tab SINCRONAMENTE (dentro il gesto utente) per non farla bloccare dal
    // popup blocker; poi naviga al blob una volta scaricato.
    const win = window.open('', '_blank');
    try {
      const resp = await axios.get(urlAllegato(circolareId, allegato), {
        headers: headers() as any,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data as Blob);
      if (win) {
        win.location.href = url;
      } else {
        // Popup bloccato: fallback a download via anchor
        const a = document.createElement('a');
        a.href = url;
        a.download = allegato.originalName || allegato.filename;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      throw await errorWithJsonBody(err);
    }
  };

  /**
   * Scarica un allegato Word o Excel con il nome originale: il browser non li
   * apre in una scheda, quindi niente finestra vuota.
   */
  const scaricaAllegatoCircolare = async (
    circolareId: string,
    allegato: CircolareAllegato
  ): Promise<void> => {
    try {
      const resp = await axios.get(urlAllegato(circolareId, allegato), {
        headers: headers() as any,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = allegato.originalName || allegato.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      throw await errorWithJsonBody(err);
    }
  };

  return { getCircolari, getAnniCircolari, getCircolare, openAllegatoCircolare, scaricaAllegatoCircolare };
};

// Istanza dell'archivio associati: token OTP dell'associato.
const circolariAssociatoApi = creaCircolariApi({
  base: BASE,
  headers: () => getAuthHeader() as Record<string, string>,
});

export const {
  getCircolari,
  getAnniCircolari,
  getCircolare,
  openAllegatoCircolare,
  scaricaAllegatoCircolare,
} = circolariAssociatoApi;

const readBlobText = (blob: Blob): Promise<string> =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

/**
 * Con responseType 'blob' anche le risposte d'errore arrivano come Blob: senza
 * rileggerle `isNonInRegola` sarebbe sempre falso durante il download. Se il
 * corpo è JSON lo rimette in `response.data`; altrimenti lascia l'errore com'è.
 */
export const errorWithJsonBody = async (err: any): Promise<any> => {
  const data = err?.response?.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return err;
  try {
    err.response.data = JSON.parse(await readBlobText(data));
  } catch {
    // corpo non JSON: si rilancia l'errore originale
  }
  return err;
};
